import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject } from 'rxjs';

import { AssignmentDetailsReturnLocationEnum } from '../../../shared/enums/assignment-details-return-location.enum';
import { AssignmentEventTypeEnum } from '../../../shared/enums/assignment-event-type.enum';
import { AssignmentsOverviewModel } from '../../models/assignments-overview.model';
import { AssignmentSummaryModalComponent } from '../assignment-summary-modal/assignment-summary-modal.component';

@Component({
    selector: 'app-assignments-list',
    templateUrl: './assignments-list.component.html',
    styleUrl: './assignments-list.component.scss'
})
export class AssignmentListComponent implements OnInit {
    @Input() eventType: AssignmentEventTypeEnum | null = null;
    @Input() assignmentSubject?: BehaviorSubject<AssignmentsOverviewModel[] | null>;
    @Input() displayFinanceInformation: boolean = false;

    @Output() assignmentsSelected = new EventEmitter<AssignmentsOverviewModel[]>();
    @Output() clicked$ = new EventEmitter<AssignmentsOverviewModel>();

    selectedAssignments: Set<string> = new Set();
    assignmentEventType = AssignmentEventTypeEnum;

    constructor(
        private router: Router,
        private bsModalService: BsModalService,
    ) { }

    ngOnInit(): void {
        this.registerEvents();
    }

    public getItemRole(assignment: AssignmentsOverviewModel): string | null {
        switch (this.eventType) {
            case AssignmentEventTypeEnum.Modal:
            case AssignmentEventTypeEnum.Checkbox:
            case AssignmentEventTypeEnum.CustomAction:
                return 'button';
            case AssignmentEventTypeEnum.RouteToPastDetails:
            case AssignmentEventTypeEnum.RouteToDetails:
                return assignment.hasDetails ? 'button' : null;
            default:
                return null;
        }
    }

    protected onAssignmentClick(assignment: AssignmentsOverviewModel): void {
        if (this.assignmentEventType == null) return;

        switch (this.eventType) {
            case AssignmentEventTypeEnum.Modal:
                this.openModal(assignment);
                break;
            case AssignmentEventTypeEnum.RouteToDetails:
                if (!assignment.hasDetails) return;
                this.routeToAssignment(assignment.id!);
                break;
            case AssignmentEventTypeEnum.RouteToPastDetails:
                if (!assignment.hasDetails) return;
                this.routeToAssignmentWithReturnLocation(assignment.id!, AssignmentDetailsReturnLocationEnum.Past);
                break;
            case AssignmentEventTypeEnum.Checkbox:
                this.toggleAssignmentSelection(assignment);
                this.assignmentsSelected;
                break;
            case AssignmentEventTypeEnum.CustomAction:
                this.emitAssignmentClicked(assignment);
                break;
            default:
                break;
        }
    }

    protected isAssignmentOnSameDay(from: Date | undefined, to: Date | undefined): boolean {
        if (!from || !to) return false;
        return new Date(from).toDateString() === new Date(to).toDateString();
    }

    protected isAssignmentSelected(assignmentUniqueId: string): boolean {
        return this.selectedAssignments.has(assignmentUniqueId);
    }

    private emitAssignmentClicked(assignment: AssignmentsOverviewModel): void {
        this.clicked$.emit(assignment);
    }

    protected toggleAssignmentSelection(assignment: AssignmentsOverviewModel): void {
        if (this.selectedAssignments.has(assignment.uniqueRandomId)) {
            this.selectedAssignments.delete(assignment.uniqueRandomId);
        } else {
            this.selectedAssignments.add(assignment.uniqueRandomId);
        }

        let selectedAssignmentList: AssignmentsOverviewModel[] = [];

        if (this.assignmentSubject?.value != null) {
            selectedAssignmentList = Array.from(this.selectedAssignments).map(uniqueRandomId =>
                (this.assignmentSubject?.value as AssignmentsOverviewModel[]).find(a => a.uniqueRandomId === uniqueRandomId)
            ) as AssignmentsOverviewModel[];
        }
        this.assignmentsSelected.emit(selectedAssignmentList);
    }

    protected getRouterLink(assignment: AssignmentsOverviewModel): string | null {
        if (this.eventType != AssignmentEventTypeEnum.RouteToDetails) return null;
        return `/assignments/${assignment.id}`;
    }

    private routeToAssignment(assignmentId: number): void {
        this.router.navigate(['/assignments', assignmentId]);
    }

    private routeToAssignmentWithReturnLocation(assignmentId: number, returnLocation: string): void {
        this.router.navigate(['/assignments', assignmentId], { queryParams: { returnLocation: returnLocation } });
    }

    private openModal(assignment: AssignmentsOverviewModel): void {
        const initialState = { assignment };
        this.bsModalService.show(AssignmentSummaryModalComponent, {
            ignoreBackdropClick: true,
            initialState
        });
    }

    private resetCheckboxes(): void {
        this.selectedAssignments.clear();
    }

    private registerEvents(): void {
        this.assignmentSubject!.subscribe(() => {
            this.resetCheckboxes();
        })
    }
}
